import "@arcgis/core/assets/esri/themes/dark/main.css";

import esriConfig from "@arcgis/core/config.js";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo.js";
import esriId from "@arcgis/core/identity/IdentityManager.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import WebMap from "@arcgis/core/WebMap.js";
import MapView from "@arcgis/core/views/MapView.js";
import Expand from "@arcgis/core/widgets/Expand.js";
import LayerList from "@arcgis/core/widgets/LayerList.js";
import Legend from "@arcgis/core/widgets/Legend.js";

import { appRootPath } from "./config";
import { configureLiveLayerRefresh } from "./map-refresh";
import {
  latestTimestamp,
  layerUrl,
  normalizeRoutes,
  normalizeTrends,
  trendWindowStart
} from "./metrics";
import type { ArcgisFeatureLike, DashboardConfig, DashboardData } from "./types";

const AUTH_ATTEMPT_KEY = "portDelayDashboard.pendingOAuthAttempt";
const AUTH_ATTEMPT_TTL_MS = 10 * 60 * 1000;

interface PendingAuthAttempt {
  portalUrl: string;
  startedAt: number;
}

const METRIC_FIELDS = [
  "calc_timestamp",
  "anchorage_queue_depth",
  "arrival_surge_6h",
  "arrival_surge_12h",
  "dwell_time_p50",
  "arrival_rate_hourly",
  "departure_rate_hourly",
  "net_accumulation",
  "traffic_intensity_pct",
  "congestion_score",
  "congestion_category",
  "score_status",
  "score_confidence_pct",
  "score_queue_contrib",
  "score_surge_contrib",
  "score_dwell_contrib",
  "score_accum_contrib",
  "occ_tuas_vessels",
  "occ_tuas_pct",
  "occ_ppanjang_vessels",
  "occ_ppanjang_pct",
  "occ_keppel_vessels",
  "occ_keppel_pct",
  "occ_brani_vessels",
  "occ_brani_pct",
  "occ_jurong_vessels",
  "occ_jurong_pct",
  "occ_jit_vessels",
  "occ_jit_pct",
  "m12_route_sort",
  "m12_route_id",
  "m12_route_name",
  "m12_origin",
  "m12_transshipment_port",
  "m12_destination",
  "m12_cargo_archetype",
  "m12_singapore_weight",
  "m12_primary_user",
  "m12_decision_context",
  "m12_delay_exposure_score",
  "m12_delay_exposure_category",
  "m12_top_driver",
  "m12_recommended_action",
  "m12_caveat"
];

const TREND_FIELDS = [
  "calc_timestamp",
  "congestion_score",
  "anchorage_queue_depth",
  "arrival_surge_6h",
  "arrival_rate_hourly",
  "departure_rate_hourly"
];

function portalSharingUrl(portalUrl: string): string {
  return `${portalUrl.replace(/\/$/, "")}/sharing`;
}

function normalizePortalUrl(portalUrl: string): string {
  return portalUrl.replace(/\/$/, "").toLowerCase();
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readPendingAuthAttempt(): PendingAuthAttempt | null {
  const storage = sessionStorageOrNull();
  if (storage === null) {
    return null;
  }
  let raw: string | null;
  try {
    raw = storage.getItem(AUTH_ATTEMPT_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PendingAuthAttempt>;
    if (typeof parsed.portalUrl === "string" && typeof parsed.startedAt === "number") {
      return {
        portalUrl: parsed.portalUrl,
        startedAt: parsed.startedAt
      };
    }
  } catch {
    clearPendingAuthAttempt();
  }
  return null;
}

function rememberPendingAuthAttempt(config: DashboardConfig): void {
  const storage = sessionStorageOrNull();
  if (storage === null) {
    return;
  }
  const attempt: PendingAuthAttempt = {
    portalUrl: config.portalUrl,
    startedAt: Date.now()
  };
  try {
    storage.setItem(AUTH_ATTEMPT_KEY, JSON.stringify(attempt));
  } catch {
    // Storage is best-effort diagnostics; sign-in should continue without it.
  }
}

export function clearPendingAuthAttempt(): void {
  try {
    sessionStorageOrNull()?.removeItem(AUTH_ATTEMPT_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function hasPendingAuthAttempt(config: DashboardConfig): boolean {
  const attempt = readPendingAuthAttempt();
  if (attempt === null) {
    return false;
  }
  const expired = Date.now() - attempt.startedAt > AUTH_ATTEMPT_TTL_MS;
  const samePortal = normalizePortalUrl(attempt.portalUrl) === normalizePortalUrl(config.portalUrl);
  if (expired || !samePortal) {
    clearPendingAuthAttempt();
    return false;
  }
  return true;
}

function toFeatureLikes(features: __esri.Graphic[]): ArcgisFeatureLike[] {
  return features.map((feature) => ({ attributes: feature.attributes }));
}

export function configureArcgis(config: DashboardConfig): void {
  esriConfig.portalUrl = config.portalUrl;
  esriConfig.assetsPath = new URL(`${appRootPath()}@arcgis/core/assets`, window.location.origin).toString();
  esriConfig.log.level = "warn";
}

export function registerOAuth(config: DashboardConfig): void {
  const info = new OAuthInfo({
    appId: config.oauthAppId,
    portalUrl: config.portalUrl,
    flowType: "auto",
    popup: false,
    preserveUrlHash: true
  });
  esriId.registerOAuthInfos([info]);
}

export async function requireCredential(config: DashboardConfig): Promise<void> {
  await esriId.checkSignInStatus(portalSharingUrl(config.portalUrl));
}

export async function signIn(config: DashboardConfig): Promise<void> {
  rememberPendingAuthAttempt(config);
  try {
    await esriId.getCredential(portalSharingUrl(config.portalUrl));
    clearPendingAuthAttempt();
  } catch (error) {
    clearPendingAuthAttempt();
    throw error;
  }
}

export function signOut(): void {
  clearPendingAuthAttempt();
  esriId.destroyCredentials();
}

export async function createMap(container: HTMLDivElement, config: DashboardConfig): Promise<MapView> {
  const map = new WebMap({
    portalItem: {
      id: config.webMapId,
      portal: {
        url: config.portalUrl
      }
    }
  });

  const view = new MapView({
    container,
    map,
    center: [103.8, 1.26],
    zoom: 11,
    constraints: {
      minZoom: 9,
      snapToZoom: false
    }
  });
  view.ui.components = ["attribution", "zoom"];
  await view.when();
  await map.loadAll();
  configureLiveLayerRefresh(
    map.allLayers.toArray().filter((layer): layer is FeatureLayer => layer instanceof FeatureLayer),
    config
  );
  addMapLayerTools(view);
  return view;
}

function addMapLayerTools(view: MapView): void {
  const legendExpand = new Expand({
    view,
    content: new Legend({ view }),
    expanded: false,
    expandIcon: "legend",
    expandTooltip: "Open map legend",
    collapseTooltip: "Close map legend",
    group: "dashboard-map-tools"
  });
  const layerListExpand = new Expand({
    view,
    content: new LayerList({ view }),
    expanded: false,
    expandIcon: "layers",
    expandTooltip: "Open layer controls",
    collapseTooltip: "Close layer controls",
    group: "dashboard-map-tools"
  });
  view.ui.add([legendExpand, layerListExpand], "top-right");
}

export async function loadDashboardData(config: DashboardConfig): Promise<DashboardData> {
  const metricsLayer = new FeatureLayer({ url: layerUrl(config.services.metricsUrl, 0) });
  const trendsLayer = new FeatureLayer({ url: layerUrl(config.services.metricsUrl, 1) });
  const vesselLayer = new FeatureLayer({ url: config.services.vesselPositionsUrl });
  const terminalsLayer = new FeatureLayer({ url: layerUrl(config.services.portInfrastructureUrl, 0) });
  const trendCutoff = new Date(trendWindowStart()).toISOString().replace("T", " ").slice(0, 19);

  const [routeFeatures, trendFeatures, vesselCount, vesselFreshness, terminalCount] = await Promise.all([
    metricsLayer.queryFeatures({
      where: "1=1",
      outFields: METRIC_FIELDS,
      returnGeometry: false,
      orderByFields: ["m12_route_sort ASC"]
    }),
    trendsLayer.queryFeatures({
      where: `calc_timestamp >= date '${trendCutoff}'`,
      outFields: TREND_FIELDS,
      returnGeometry: false,
      orderByFields: ["calc_timestamp DESC"],
      num: 96
    }),
    vesselLayer.queryFeatureCount({ where: "1=1" }).catch(() => null),
    vesselLayer.queryFeatures({
      where: "1=1",
      outStatistics: [{
        statisticType: "max",
        onStatisticField: "timestamp",
        outStatisticFieldName: "latest_timestamp"
      }],
      returnGeometry: false
    }).catch(() => null),
    terminalsLayer.queryFeatureCount({ where: "1=1" }).catch(() => null)
  ]);

  return {
    routes: normalizeRoutes(toFeatureLikes(routeFeatures.features)),
    trends: normalizeTrends(toFeatureLikes(trendFeatures.features)),
    vesselCount,
    vesselPositionTimestamp: latestTimestamp(
      toFeatureLikes(vesselFreshness?.features ?? []),
      "latest_timestamp"
    ),
    terminalCount
  };
}
